import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from 'api';
import type { SummaryReportGroup } from 'api/types/reports.types';
import React, { useMemo, useState } from 'react';
import type { TooltipPayload } from 'recharts/types/state/tooltipSlice';
import {
  BarChartCard,
  BarChartDatum,
  BarDef,
  BILLABLE_COLOR,
  ChartDetailModal,
  ChartTooltip,
  ChartTooltipRow,
  FALLBACK_PROJECT_COLOR,
  NON_BILLABLE_COLOR,
  formatBarLabel,
  formatBarLabelWithDay,
  generateDateKeys,
  granularityToGroupBy,
  resolveChartGranularity,
  secondsToHMS,
  secondsToHours,
} from 'shared/components/charts';
import { queryKeys } from 'shared/constants/queryKeys';
import { displayDuration } from 'shared/lib/formatDuration';

import {
  chartCardSx,
  chartModeDropdownSx,
  chartMenuItemSx,
  chartModeValueSx,
  chartModeViewBySx,
  ChartHeaderRight,
  ChartHeaderRow,
  ChartLegendDot,
  ChartLegendItem,
  ChartLegendRow,
  ChartOuterBox,
  ChartTitle,
} from '../../reports.styles';
import { CHART_MODES } from '../../types/reports.types';
import type { ChartMode, ReportFiltersState } from '../../types/reports.types';
import { buildBillabilityTooltipRows, buildProjectTooltipRows } from '../../utils/reportsUtils';

// ── Constants ────────────────────────────────────────────────────────────────

const MAX_TOOLTIP_ROWS = 10;

const BILLABILITY_BARS: BarDef[] = [
  { dataKey: 'billable', color: BILLABLE_COLOR, label: 'Billable' },
  { dataKey: 'nonBillable', color: NON_BILLABLE_COLOR, label: 'Non-billable' },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  startDate: string;
  endDate: string;
  filters: ReportFiltersState;
  projects: Array<{ id: string; name: string; color?: string }>;
}

const SummaryBarChart: React.FC<Props> = ({ startDate, endDate, filters, projects }) => {
  const [mode, setMode] = useState<ChartMode>('Billability');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [modalDatum, setModalDatum] = useState<BarChartDatum | null>(null);

  const granularity = resolveChartGranularity(startDate, endDate);
  const isProjectMode = mode === 'Project';

  // ── Base API params ─────────────────────────────────────────────────────────
  const baseParams = {
    from: startDate,
    to: endDate,
    userIds: filters.userIds?.join(','),
    projectIds: filters.projectIds?.join(','),
    clientIds: filters.clientIds?.join(','),
    isBillable: filters.billable === null ? undefined : filters.billable,
    hasDescription: filters.hasDescription,
    descriptionSearch: filters.descriptionSearch || undefined,
  };

  // ── Queries ─────────────────────────────────────────────────────────────────
  const billabilityParams = { ...baseParams, groupBy: granularityToGroupBy(granularity) };
  const { data: billabilityData, isLoading: billabilityLoading } = useQuery({
    queryKey: queryKeys.reports.summary(billabilityParams),
    queryFn: async () => (await reportsService.getSummary(billabilityParams)).data.data,
    staleTime: 5 * 60 * 1000,
    enabled: !!startDate,
  });

  const projectParams = { ...baseParams, groupBy: granularityToGroupBy(granularity, 'Project') };
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: queryKeys.reports.summary(projectParams),
    queryFn: async () => (await reportsService.getSummary(projectParams)).data.data,
    staleTime: 5 * 60 * 1000,
    enabled: !!startDate && isProjectMode,
  });

  // ── Project lookup map ──────────────────────────────────────────────────────
  const projectMap = useMemo(() => {
    const m = new Map<string, { name: string; color: string }>();
    projects.forEach((p) => m.set(p.id, { name: p.name, color: p.color || FALLBACK_PROJECT_COLOR }));
    return m;
  }, [projects]);

  // ── Chart data transforms ───────────────────────────────────────────────────
  const billabilityChartData = useMemo((): BarChartDatum[] => {
    const allKeys = generateDateKeys(startDate, endDate, granularity);
    const byKey = new Map<string, SummaryReportGroup>();
    billabilityData?.groups?.forEach((g: SummaryReportGroup) => { if (g.key) byKey.set(g.key, g); });
    return allKeys.map((key) => {
      const g = byKey.get(key);
      return {
        key,
        label: formatBarLabel(key),
        billable: g ? secondsToHours(g.billableDurationSecs) : 0,
        nonBillable: g ? secondsToHours(g.totalDurationSecs - g.billableDurationSecs) : 0,
        _totalSecs: g?.totalDurationSecs ?? 0,
        _billableSecs: g?.billableDurationSecs ?? 0,
      };
    });
  }, [billabilityData, startDate, endDate, granularity]);

  const { projectChartData, projectBars } = useMemo((): {
    projectChartData: BarChartDatum[];
    projectBars: BarDef[];
  } => {
    if (!projectData?.groups) return { projectChartData: [], projectBars: [] };

    const projectIds = new Set<string>();
    projectData.groups.forEach((dg: SummaryReportGroup) => {
      dg.subGroups?.forEach((pg) => { if (pg.key) projectIds.add(pg.key); });
    });

    const bars: BarDef[] = Array.from(projectIds).map((id) => {
      const proj = projectMap.get(id);
      return { dataKey: id, color: proj?.color || FALLBACK_PROJECT_COLOR, label: proj?.name || 'Unknown project' };
    });

    const byKey = new Map<string, SummaryReportGroup>();
    projectData.groups.forEach((g: SummaryReportGroup) => { if (g.key) byKey.set(g.key, g); });

    const chartData: BarChartDatum[] = generateDateKeys(startDate, endDate, granularity).map((key) => {
      const dg = byKey.get(key);
      const datum: BarChartDatum = { key, label: formatBarLabel(key), _totalSecs: dg?.totalDurationSecs ?? 0 };
      dg?.subGroups?.forEach((pg) => { if (pg.key) datum[pg.key] = secondsToHours(pg.totalDurationSecs); });
      projectIds.forEach((id) => { if (datum[id] === undefined) datum[id] = 0; });
      return datum;
    });

    return { projectChartData: chartData, projectBars: bars };
  }, [projectData, projectMap, startDate, endDate, granularity]);

  // ── Tooltip renderers ───────────────────────────────────────────────────────
  const renderBillabilityTooltip = (payload: TooltipPayload | undefined): React.ReactNode => {
    const datum = payload?.[0]?.payload;
    if (!datum) return null;
    const totalSecs: number = datum._totalSecs ?? 0;
    const rows = buildBillabilityTooltipRows(datum, totalSecs);
    return <ChartTooltip title={formatBarLabelWithDay(datum.key as string)} total={displayDuration(secondsToHMS(totalSecs))} rows={rows} />;
  };

  const renderProjectTooltip = (payload: TooltipPayload | undefined): React.ReactNode => {
    const datum = payload?.[0]?.payload;
    if (!datum) return null;
    const totalSecs: number = datum._totalSecs ?? 0;
    const { rows, overflow } = buildProjectTooltipRows(datum, projectBars, totalSecs, MAX_TOOLTIP_ROWS);
    return (
      <ChartTooltip
        title={formatBarLabelWithDay(datum.key as string)}
        total={displayDuration(secondsToHMS(totalSecs))}
        rows={rows}
        overflow={overflow}
      />
    );
  };

  // ── Modal rows builder ──────────────────────────────────────────────────────
  const buildModalRows = (datum: BarChartDatum): ChartTooltipRow[] => {
    const totalSecs = (datum._totalSecs as number) ?? 0;
    if (!isProjectMode) return buildBillabilityTooltipRows(datum, totalSecs);
    return buildProjectTooltipRows(datum, projectBars, totalSecs).rows;
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const chartData = isProjectMode ? projectChartData : billabilityChartData;
  const bars = isProjectMode ? projectBars : BILLABILITY_BARS;
  const loading = isProjectMode ? projectLoading : billabilityLoading;
  const tooltipRenderer = isProjectMode ? renderProjectTooltip : renderBillabilityTooltip;

  const modalRows = modalDatum ? buildModalRows(modalDatum) : [];
  const modalTitle = modalDatum ? formatBarLabelWithDay(modalDatum.key as string) : '';
  const modalTotal = modalDatum ? displayDuration(secondsToHMS((modalDatum._totalSecs as number) ?? 0)) : '';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <ChartOuterBox>
      <Box sx={chartCardSx}>
        {/* Header row */}
        <ChartHeaderRow>
          <ChartTitle sx={{ color: 'text.primary' }}>Daily Hours</ChartTitle>

          {/* Right: legend + mode dropdown */}
          <ChartHeaderRight>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: '14px !important' }} />}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={chartModeDropdownSx}
            >
              <Box component="span" sx={chartModeViewBySx}>View by</Box>
              <Box component="span" sx={chartModeValueSx}>{mode}</Box>
            </Button>

            {/* Legend — only for billability mode */}
            {!isProjectMode && (
              <ChartLegendRow>
                {BILLABILITY_BARS.map((bar) => (
                  <ChartLegendItem key={bar.dataKey}>
                    <ChartLegendDot dotcolor={bar.color} />
                    <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>{bar.label}</Box>
                  </ChartLegendItem>
                ))}
              </ChartLegendRow>
            )}
          </ChartHeaderRight>
        </ChartHeaderRow>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {CHART_MODES.map((option) => (
            <MenuItem
              key={option}
              selected={mode === option}
              onClick={() => { setMode(option); setMenuAnchor(null); }}
              sx={chartMenuItemSx}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>

        {/* Chart */}
        <BarChartCard
          data={chartData}
          bars={bars}
          height={320}
          loading={loading}
          tooltipContent={tooltipRenderer}
          onBarClick={(datum) => setModalDatum(datum)}
        />
      </Box>

      {/* Detail modal */}
      <ChartDetailModal
        open={modalDatum !== null}
        onClose={() => setModalDatum(null)}
        title={modalTitle}
        total={modalTotal}
        rows={modalRows}
      />
    </ChartOuterBox>
  );
};

export default SummaryBarChart;
